import React from "react";
import { Link as RouterLink } from "react-router-dom";
import { Alert, AlertTitle, Box, Link, Typography } from "@mui/material";
import moment from "moment/moment";

const PREVIOUS_STATUS_LABELS = {
  OPEN: "Abierto (sin finalizar)",
  DONE: "Finalizado",
};

// cancelledAt arrives as an ISO string through the cloud function, as a
// Firestore Timestamp when the doc is read directly, or serialized as
// { _seconds } when a Timestamp went through JSON untouched.
const toMoment = (value) => {
  if (!value) return null;
  if (value.toDate) return moment(value.toDate());
  if (value._seconds !== undefined) return moment(value._seconds * 1000);
  const m = moment(value);
  return m.isValid() ? m : null;
};

// Resolves who/when/why for an annulled radicado. Radicados annulled before the
// motive was required carry nothing; the PQRSD requerimiento path writes the
// flat cancelledAt/cancelledReason pair instead of `cancellation`.
export const getCancellationInfo = (filed) => {
  const c = filed?.cancellation;
  if (c) {
    const by = c.cancelledBy || {};
    return {
      reason: c.reason || null,
      at: toMoment(c.cancelledAt),
      byName: `${by.name || ""} ${by.lastName || ""}`.trim() || null,
      byEmail: by.email || null,
      previousStatus: c.previousStatus || null,
    };
  }
  if (filed?.cancelledReason || filed?.cancelledAt) {
    return {
      reason: filed.cancelledReason || null,
      at: toMoment(filed.cancelledAt),
      byName: "Sistema",
      byEmail: null,
      previousStatus: null,
    };
  }
  return null;
};

// What the annulment email did, for staff: recorded by CANCEL_FILED on
// cancellation.notification. null when there is nothing worth saying — an open
// radicado was never sent to anyone, and annulments before the email existed
// have no record.
export const getNotificationSummary = (filed) => {
  const n = filed?.cancellation?.notification;
  if (!n || !n.status) return null;
  const sent = Number(n.sentCount) || 0;
  const failed = Array.isArray(n.failed) ? n.failed : [];
  const plural = (count) => `${count} ${count === 1 ? "correo" : "correos"}`;
  switch (n.status) {
    case "sent":
      return { severity: "ok", text: `Se notificó la anulación a ${plural(sent)}.` };
    case "partial":
      return {
        severity: "warning",
        text: `Se notificó a ${plural(sent)}; no se pudo enviar a: ${failed.join(", ")}.`,
      };
    case "failed":
      return {
        severity: "warning",
        text: failed.length
          ? `No se pudo enviar la notificación a: ${failed.join(", ")}.`
          : "No se pudo enviar la notificación.",
      };
    case "disabled":
      return { severity: "info", text: "No se envió notificación: estaba desactivada en la configuración." };
    case "no_addresses":
      return { severity: "info", text: "No se envió notificación: ningún destinatario tenía correo." };
    default:
      return null;
  }
};

function Row({ label, children }) {
  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 0.5 }}>
      <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 110 }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ flex: 1, minWidth: 0, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
        {children}
      </Typography>
    </Box>
  );
}

// showActor: the public QR page is reachable without logging in, and who
// annulled a radicado (a staff member's name and email) is not public data.
export default function FiledCancellationNotice({
  filed,
  title = "RADICADO ANULADO",
  linkToFiled = false,
  showActor = true,
  showPreviousStatus = true,
}) {
  const info = getCancellationInfo(filed);
  const notification = showActor ? getNotificationSummary(filed) : null;
  return (
    <Alert severity="error" variant="outlined" sx={{ mb: 2, "& .MuiAlert-message": { width: "100%" } }}>
      <AlertTitle sx={{ fontWeight: 700 }}>{title}</AlertTitle>
      {info ? (
        <>
          <Row label="Motivo">{info.reason || "—"}</Row>
          {showActor && (
            <Row label="Anulado por">
              {info.byName || "—"}
              {info.byEmail ? ` <${info.byEmail}>` : ""}
            </Row>
          )}
          <Row label="Fecha">{info.at ? info.at.format("LL, h:mm:ss a") : "—"}</Row>
          {showPreviousStatus && info.previousStatus && (
            <Row label="Estado previo">{PREVIOUS_STATUS_LABELS[info.previousStatus] || info.previousStatus}</Row>
          )}
          {notification && (
            <Row label="Notificación">
              <Box
                component="span"
                sx={{ color: notification.severity === "warning" ? "warning.dark" : "text.primary" }}
              >
                {notification.text}
              </Box>
            </Row>
          )}
        </>
      ) : (
        <Typography variant="body2">Anulado antes de que se registrara el motivo.</Typography>
      )}
      {linkToFiled && filed?.filedId && (
        <Box sx={{ mt: 1 }}>
          <Link component={RouterLink} to={`/filed/cancelled/${encodeURIComponent(filed.filedId)}`}>
            Ver radicado anulado
          </Link>
        </Box>
      )}
    </Alert>
  );
}
